import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'react-toastify';
import { FaUpload, FaLocationArrow, FaTag, FaList, FaPaw, FaHashtag, FaBirthdayCake, FaWeight, FaRupeeSign, FaAlignLeft, FaMapMarkerAlt, FaCity, FaMapPin, FaImages, FaEdit } from 'react-icons/fa';
import { getStates, getCitiesByState, getCurrentLocation, getLocationFromCoordinates } from '../services/locationService';
import { API_ENDPOINTS, getImageUrl } from '../config/api';
import BackToHome from '../components/BackToHome';
import './EditAd.css';
import { sanitizeInput, sanitizeFormData } from '../utils/security';

const EditAd = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    price: '',
    priceType: 'Per Unit',
    description: '',
    images: [],
    state: '',
    city: '',
    neighbourhood: '',
    type: '',
    breed: '',
    quantity: '',
    age: '',
    ageUnit: 'Years',
    weight: '',
    weightUnit: 'Kgs',
    milkCapacity: '',
    gender: '',
    status: '',
    runningMonth: '',
    size: ''
  });
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [newImages, setNewImages] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [removedImages, setRemovedImages] = useState([]);

  useEffect(() => {
    setStates(getStates());
  }, []);

  useEffect(() => {
    if (formData.state) {
      const citiesList = getCitiesByState(formData.state);
      setCities(citiesList);
    } else {
      setCities([]);
    }
  }, [formData.state]);

  useEffect(() => {
    if (formData.city && cities.length > 0) {
      const selectedCity = cities.find(city => city.name === formData.city);
      setNeighborhoods(selectedCity && Array.isArray(selectedCity.neighborhoods) ? selectedCity.neighborhoods : []);
    } else {
      setNeighborhoods([]);
    }
  }, [formData.city, cities]);

  useEffect(() => {
    const fetchAd = async () => {
      try {
        const token = await currentUser.getIdToken();
        const response = await axios.get(API_ENDPOINTS.ADS.GET_BY_ID(id), {
          headers: { 
            Authorization: `Bearer ${token}` 
          }
        });

        const adData = response.data;

        // Parse location
        let adState = adData.state || '';
        let adCity = adData.city || '';
        let adNeighbourhood = adData.neighbourhood || adData.area || '';

        // Parse age with unit
        let ageValue = '';
        let ageUnit = 'Years';
        if (adData.age) {
          const ageStr = adData.age.toString();
          const ageMatch = ageStr.match(/^(\d+(?:\.\d+)?)\s*(Years?|Months?)/i);
          if (ageMatch) {
            ageValue = ageMatch[1];
            ageUnit = ageMatch[2].toLowerCase().includes('month') ? 'Months' : 'Years';
          } else {
            ageValue = ageStr;
          }
        }

        // Parse weight with unit
        let weightValue = '';
        let weightUnit = 'Kgs';
        if (adData.weight) {
          const weightStr = adData.weight.toString();
          const weightNum = parseFloat(weightStr);
          if (!isNaN(weightNum)) {
            weightValue = weightNum.toString();
            // Assume kgs if no unit specified
            weightUnit = 'Kgs';
          }
        }

        // Parse milk capacity
        let milkCapacity = '';
        if (adData.milkCapacity) {
          const milkStr = adData.milkCapacity.toString();
          const rangeMatch = milkStr.match(/(Below 5|5-10|10-15|15-20|20\+)/);
          if (rangeMatch) {
            milkCapacity = rangeMatch[1];
          } else {
            milkCapacity = milkStr;
          }
        }

        // Parse running month
        let runningMonth = '';
        if (adData.runningMonth) {
          const monthMatch = adData.runningMonth.toString().match(/^(\d+)/);
          if (monthMatch) {
            runningMonth = monthMatch[1];
          }
        }

        // Parse status
        let status = '';
        if (adData.cattleStatus || adData.status) {
          const statusValue = (adData.cattleStatus || adData.status).toLowerCase();
          if (statusValue === 'milking') status = 'milk';
          else if (statusValue === 'pregnant') status = 'pregnancy';
          else if (statusValue === 'dry') status = 'dry';
          else status = statusValue;
        }

        setFormData({
          title: adData.title || '',
          category: adData.category || '',
          price: adData.price?.toString() || '',
          priceType: adData.priceType || 'Per Unit',
          description: adData.description || '',
          images: adData.images || [],
          state: adState,
          city: adCity,
          neighbourhood: adNeighbourhood,
          type: adData.type || '',
          breed: adData.breed || '',
          quantity: adData.quantity?.toString() || '',
          age: ageValue,
          ageUnit: ageUnit,
          weight: weightValue,
          weightUnit: weightUnit,
          milkCapacity: milkCapacity,
          gender: adData.gender || '',
          status: status,
          runningMonth: runningMonth,
          size: adData.size || ''
        });
        setLoading(false);
      } catch (error) {
        console.error('Error fetching ad:', error);
        setError('Failed to fetch ad details');
        toast.error('Failed to fetch ad details');
        setLoading(false);
      }
    };

    if (currentUser && id) {
      fetchAd();
    }
  }, [id, currentUser]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Sanitize input based on field type
    let sanitizedValue = value;
    if (typeof value === 'string') {
      // Sanitize text inputs to prevent XSS
      if (name === 'title' || name === 'description' || name === 'breed' || name === 'type' || name === 'size') {
        sanitizedValue = sanitizeInput(value);
      } else if (name === 'price' || name === 'age' || name === 'weight' || name === 'quantity') {
        // For numeric fields, only allow numbers and decimal points
        sanitizedValue = value.replace(/[^0-9.]/g, '');
      } else {
        // For other text fields, sanitize
        sanitizedValue = sanitizeInput(value);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue,
      ...(name === 'state' && { city: '', neighbourhood: '' }),
      ...(name === 'city' && { neighbourhood: '' }),
      ...(name === 'status' && { milkCapacity: '', runningMonth: '' })
    }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setNewImages(prev => [...prev, ...files].slice(0, 5));
  };

  const handleRemoveExistingImage = (imageName) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter(img => img !== imageName)
    }));
    setRemovedImages(prev => [...prev, imageName]);
  };

  const handleRemoveNewImage = (index) => {
    setNewImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleUseCurrentLocation = async () => {
    try {
      const position = await getCurrentLocation();
      const location = await getLocationFromCoordinates(position.latitude, position.longitude);
      setFormData(prev => ({
        ...prev,
        state: location.state,
        city: location.city,
        neighbourhood: location.neighborhood
      }));
      toast.success('Location updated successfully');
    } catch (error) {
      console.error('Error getting current location:', error);
      toast.error('Failed to get current location. Please select manually.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Removed validation - user can update any field(s) they want
    // Backend will handle validation if needed

    try {
      setSaving(true);
      
      // Sanitize all form data before sending
      const sanitizedFormData = sanitizeFormData(formData);
      
      const token = await currentUser.getIdToken();
      const formDataToSend = new FormData();

      // Always send required fields (backend requires these) - use sanitized values
      formDataToSend.append('title', sanitizedFormData.title || '');
      formDataToSend.append('category', sanitizedFormData.category || '');
      formDataToSend.append('price', sanitizedFormData.price ? parseFloat(sanitizedFormData.price) : 0);
      formDataToSend.append('description', sanitizedFormData.description || '');
      formDataToSend.append('state', sanitizedFormData.state || '');
      formDataToSend.append('city', sanitizedFormData.city || '');
      
      // Optional fields (sanitized)
      if (sanitizedFormData.priceType) formDataToSend.append('priceType', sanitizedFormData.priceType);
      if (sanitizedFormData.neighbourhood) formDataToSend.append('neighbourhood', sanitizedFormData.neighbourhood);
      if (sanitizedFormData.type) formDataToSend.append('type', sanitizedFormData.type);
      if (sanitizedFormData.breed) formDataToSend.append('breed', sanitizedFormData.breed);
      if (sanitizedFormData.quantity) formDataToSend.append('quantity', parseFloat(sanitizedFormData.quantity));
      
      // Age with unit (sanitized)
      if (sanitizedFormData.age) {
        const sanitizedAge = sanitizeInput(sanitizedFormData.age.toString());
        const ageValue = `${sanitizedAge} ${sanitizedFormData.ageUnit}`;
        formDataToSend.append('age', ageValue);
      } else {
        formDataToSend.append('age', '');
      }

      // Weight with unit conversion (sanitized)
      if (sanitizedFormData.weight) {
        const weightNum = parseFloat(sanitizedFormData.weight);
        if (!isNaN(weightNum)) {
          const weightInKg = sanitizedFormData.weightUnit === 'Grams' ? weightNum / 1000 : weightNum;
          formDataToSend.append('weight', weightInKg.toString());
        } else {
          formDataToSend.append('weight', '');
        }
      } else {
        formDataToSend.append('weight', '');
      }

      // Gender - only send if it has a value and is for a category that needs it
      // Don't send empty string as backend validation rejects it
      if (formData.gender && formData.gender.trim() !== '') {
        const genderMap = { 'male': 'Male', 'female': 'Female', 'pair': 'Pair' };
        const genderValue = genderMap[formData.gender] || formData.gender;
        // Only send gender for categories that require it (pets except Aquarium Fishes, and sheep-goat)
        const shouldSendGender = (formData.category === 'pets' && formData.type !== 'Aquarium Fishes') || 
                                 formData.category === 'sheep-goat';
        if (shouldSendGender) {
          formDataToSend.append('gender', genderValue);
        }
      }
      // If gender is empty, don't send it at all (backend will handle it)

      // Status for cattle
      if (formData.status && (formData.type === 'Cows' || formData.type === 'Buffalo')) {
        const statusMap = { 'milk': 'milking', 'pregnancy': 'pregnant', 'dry': 'dry' };
        formDataToSend.append('cattleStatus', statusMap[formData.status] || formData.status);
      }

      // Milk capacity - only send when status is 'milk', otherwise send placeholder for backend validation
      if (formData.status === 'milk' && formData.milkCapacity) {
        formDataToSend.append('milkCapacity', `${formData.milkCapacity} liters per day`);
      } else if (formData.status && (formData.type === 'Cows' || formData.type === 'Buffalo')) {
        // Backend requires milkCapacity for all Cow/Buffalo, send placeholder for non-milking statuses
        formDataToSend.append('milkCapacity', '0 liters per day');
      }

      // Running month - only send when status is 'pregnancy'
      if (formData.status === 'pregnancy' && formData.runningMonth) {
        formDataToSend.append('runningMonth', `${formData.runningMonth} ${formData.runningMonth === '1' ? 'Month' : 'Months'}`);
      }

      if (sanitizedFormData.size) formDataToSend.append('size', sanitizedFormData.size);

      // Existing images
      formDataToSend.append('existingImages', JSON.stringify(formData.images));

      // New images
      newImages.forEach(file => {
        formDataToSend.append('images', file);
      });

      const response = await axios.put(
        API_ENDPOINTS.ADS.UPDATE(id),
        formDataToSend,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data) {
        toast.success('Ad updated successfully');
        navigate('/my-ads');
      }
    } catch (error) {
      console.error('Error updating ad:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      console.error('Full error response:', JSON.stringify(error.response?.data, null, 2));
      
      // Extract error message from various possible locations
      let errorMessage = 'Failed to update ad';
      if (error.response?.data) {
        const responseData = error.response.data;
        
        // Handle string response
        if (typeof responseData === 'string') {
          errorMessage = responseData;
        }
        // Handle object with error field
        else if (responseData.error) {
          errorMessage = responseData.error;
          // If there are details or fields, append them
          if (responseData.details && Array.isArray(responseData.details)) {
            errorMessage += ': ' + responseData.details.join(', ');
          } else if (responseData.fields && Array.isArray(responseData.fields)) {
            errorMessage += ': Missing fields - ' + responseData.fields.join(', ');
          }
        }
        // Handle object with message field
        else if (responseData.message) {
          errorMessage = responseData.message;
        }
        // Handle array of validation errors
        else if (Array.isArray(responseData) && responseData.length > 0) {
          errorMessage = responseData.map(err => 
            typeof err === 'string' ? err : err.message || err.msg || JSON.stringify(err)
          ).join(', ');
        }
        // Handle object - try to extract meaningful error
        else if (typeof responseData === 'object') {
          const errorKeys = Object.keys(responseData);
          if (errorKeys.length > 0) {
            // Prefer 'error' or 'message' keys
            if (responseData.error) {
              errorMessage = responseData.error;
            } else if (responseData.message) {
              errorMessage = responseData.message;
            } else {
              // Fallback: show all key-value pairs
              errorMessage = errorKeys.map(key => {
                const value = responseData[key];
                if (Array.isArray(value)) {
                  return `${key}: ${value.join(', ')}`;
                }
                return `${key}: ${typeof value === 'string' ? value : JSON.stringify(value)}`;
              }).join('; ');
            }
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <BackToHome />
        <div className="text-center my-5">
          <p>Loading ad details...</p>
        </div>
      </Container>
    );
  }

  const isCattleCowOrBuffalo = formData.category === 'cattle' && (formData.type === 'Cows' || formData.type === 'Buffalo');

  return (
    <Container className="edit-ad-container">
      <BackToHome />
      <div className="edit-ad-form-wrapper">
        <h2 className="edit-ad-title">
          <FaEdit className="me-2" />
          Edit Ad
        </h2>

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        <Form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="form-section">
            <h5 className="section-title">Basic Information</h5>
            
            <Form.Group className="mb-3">
              <Form.Label><FaTag className="me-2" />Title</Form.Label>
              <Form.Control
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter ad title"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><FaAlignLeft className="me-2" />Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Enter description"
              />
            </Form.Group>

            <Row>
              <Col md={8}>
                <Form.Group className="mb-3">
                  <Form.Label><FaRupeeSign className="me-2" />Price (₹)</Form.Label>
                  <Form.Control
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="Enter price"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Price Type</Form.Label>
                  <Form.Select
                    name="priceType"
                    value={formData.priceType}
                    onChange={handleChange}
                  >
                    <option value="Per Unit">Per Unit</option>
                    <option value="For All">For All</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* Additional Details */}
          <div className="form-section">
            <h5 className="section-title">Additional Details</h5>

            {formData.category === 'chickens' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label><FaList className="me-2" />Type</Form.Label>
                  <Form.Select name="type" value={formData.type} onChange={handleChange}>
                    <option value="">Select Type</option>
                    <option value="Hen">Hen</option>
                    <option value="Rooster">Rooster</option>
                    <option value="Chicks">Chicks</option>
                    <option value="Breeding Pair">Breeding Pair</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><FaPaw className="me-2" />Breed (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="breed"
                    value={formData.breed}
                    onChange={handleChange}
                    placeholder="Aseel, Kadaknath, Country, etc."
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><FaHashtag className="me-2" />Quantity</Form.Label>
                      <Form.Control
                        type="number"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        placeholder="Enter quantity"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><FaBirthdayCake className="me-2" />Age (Optional)</Form.Label>
                      <Row>
                        <Col>
                          <Form.Control
                            type="number"
                            name="age"
                            value={formData.age}
                            onChange={handleChange}
                            placeholder="Age"
                          />
                        </Col>
                        <Col xs="auto">
                          <Form.Select
                            name="ageUnit"
                            value={formData.ageUnit}
                            onChange={handleChange}
                            style={{ width: '100px' }}
                          >
                            <option value="Years">Years</option>
                            <option value="Months">Months</option>
                          </Form.Select>
                        </Col>
                      </Row>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label><FaWeight className="me-2" />Weight (Optional)</Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        type="number"
                        step="0.1"
                        name="weight"
                        value={formData.weight}
                        onChange={handleChange}
                        placeholder="Weight"
                      />
                    </Col>
                    <Col xs="auto">
                      <Form.Select
                        name="weightUnit"
                        value={formData.weightUnit}
                        onChange={handleChange}
                        style={{ width: '100px' }}
                      >
                        <option value="Kgs">Kgs</option>
                        <option value="Grams">Grams</option>
                      </Form.Select>
                    </Col>
                  </Row>
                </Form.Group>
              </>
            )}

            {formData.category === 'pets' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label><FaList className="me-2" />Type</Form.Label>
                  <Form.Select name="type" value={formData.type} onChange={handleChange}>
                    <option value="">Select Type</option>
                    <option value="Dogs">Dogs</option>
                    <option value="Puppies">Puppies</option>
                    <option value="Cats">Cats</option>
                    <option value="Rabbits">Rabbits</option>
                    <option value="Birds">Birds</option>
                    <option value="Aquarium Fishes">Aquarium Fishes</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><FaPaw className="me-2" />Breed/Name (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="breed"
                    value={formData.breed}
                    onChange={handleChange}
                    placeholder={formData.type === 'Aquarium Fishes' ? 'Fish Name' : formData.type === 'Birds' ? 'Bird Name' : 'Breed'}
                  />
                </Form.Group>

                {formData.type === 'Aquarium Fishes' ? (
                  <>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label><FaBirthdayCake className="me-2" />Age (Optional)</Form.Label>
                          <Row>
                            <Col>
                              <Form.Control
                                type="number"
                                name="age"
                                value={formData.age}
                                onChange={handleChange}
                                placeholder="Age"
                              />
                            </Col>
                            <Col xs="auto">
                              <Form.Select
                                name="ageUnit"
                                value={formData.ageUnit}
                                onChange={handleChange}
                                style={{ width: '100px' }}
                              >
                                <option value="Years">Years</option>
                                <option value="Months">Months</option>
                              </Form.Select>
                            </Col>
                          </Row>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Size (Optional)</Form.Label>
                          <Form.Control
                            type="text"
                            name="size"
                            value={formData.size}
                            onChange={handleChange}
                            placeholder="Size"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Label><FaHashtag className="me-2" />Quantity</Form.Label>
                      <Form.Control
                        type="number"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        placeholder="Enter quantity"
                      />
                    </Form.Group>
                  </>
                ) : (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label><FaBirthdayCake className="me-2" />Age (Optional)</Form.Label>
                      <Row>
                        <Col>
                          <Form.Control
                            type="number"
                            name="age"
                            value={formData.age}
                            onChange={handleChange}
                            placeholder="Age"
                          />
                        </Col>
                        <Col xs="auto">
                          <Form.Select
                            name="ageUnit"
                            value={formData.ageUnit}
                            onChange={handleChange}
                            style={{ width: '100px' }}
                          >
                            <option value="Years">Years</option>
                            <option value="Months">Months</option>
                          </Form.Select>
                        </Col>
                      </Row>
                    </Form.Group>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label><FaHashtag className="me-2" />Quantity</Form.Label>
                          <Form.Control
                            type="number"
                            name="quantity"
                            value={formData.quantity}
                            onChange={handleChange}
                            placeholder="Enter quantity"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Gender</Form.Label>
                          <Form.Select name="gender" value={formData.gender} onChange={handleChange}>
                            <option value="">Select Gender</option>
                            <option value="male">Male</option>
                            <option value="female">Female</option>
                            <option value="pair">Pair</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>
                  </>
                )}
              </>
            )}

            {formData.category === 'cattle' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label><FaList className="me-2" />Type</Form.Label>
                  <Form.Select name="type" value={formData.type} onChange={handleChange}>
                    <option value="">Select Type</option>
                    <option value="Cows">Cows</option>
                    <option value="Ox">Ox</option>
                    <option value="Buffalo">Buffalo</option>
                    <option value="Calves">Calves</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><FaPaw className="me-2" />Breed (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="breed"
                    value={formData.breed}
                    onChange={handleChange}
                    placeholder="Jersey, Sahiwal, Murrah, etc."
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><FaBirthdayCake className="me-2" />Age (Optional)</Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleChange}
                        placeholder="Age"
                      />
                    </Col>
                    <Col xs="auto">
                      <Form.Select
                        name="ageUnit"
                        value={formData.ageUnit}
                        onChange={handleChange}
                        style={{ width: '100px' }}
                      >
                        <option value="Years">Years</option>
                        <option value="Months">Months</option>
                      </Form.Select>
                    </Col>
                  </Row>
                </Form.Group>

                {isCattleCowOrBuffalo && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Status</Form.Label>
                      <Form.Select name="status" value={formData.status} onChange={handleChange}>
                        <option value="">Select Status</option>
                        <option value="milk">Milking</option>
                        <option value="pregnancy">Pregnant</option>
                        <option value="dry">Dry</option>
                      </Form.Select>
                    </Form.Group>

                    {formData.status === 'milk' && (
                      <Form.Group className="mb-3">
                        <Form.Label>Milk Capacity</Form.Label>
                        <Form.Select name="milkCapacity" value={formData.milkCapacity} onChange={handleChange}>
                          <option value="">Select Capacity</option>
                          <option value="Below 5">Below 5 liters per day</option>
                          <option value="5-10">5-10 liters per day</option>
                          <option value="10-15">10-15 liters per day</option>
                          <option value="15-20">15-20 liters per day</option>
                          <option value="20+">20+ liters per day</option>
                        </Form.Select>
                      </Form.Group>
                    )}

                    {formData.status === 'pregnancy' && (
                      <Form.Group className="mb-3">
                        <Form.Label>Running Month</Form.Label>
                        <Form.Select name="runningMonth" value={formData.runningMonth} onChange={handleChange}>
                          <option value="">Select Month</option>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                            <option key={num} value={num.toString()}>
                              {num} {num === 1 ? 'Month' : 'Months'}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    )}
                  </>
                )}
              </>
            )}

            {formData.category === 'sheep-goat' && (
              <>
                <Form.Group className="mb-3">
                  <Form.Label><FaList className="me-2" />Type</Form.Label>
                  <Form.Select name="type" value={formData.type} onChange={handleChange}>
                    <option value="">Select Type</option>
                    <option value="Sheep">Sheep</option>
                    <option value="Goat">Goat</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><FaPaw className="me-2" />Breed (Optional)</Form.Label>
                  <Form.Control
                    type="text"
                    name="breed"
                    value={formData.breed}
                    onChange={handleChange}
                    placeholder="Boer, Jamunapari, etc."
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><FaBirthdayCake className="me-2" />Age (Optional)</Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        type="number"
                        name="age"
                        value={formData.age}
                        onChange={handleChange}
                        placeholder="Age"
                      />
                    </Col>
                    <Col xs="auto">
                      <Form.Select
                        name="ageUnit"
                        value={formData.ageUnit}
                        onChange={handleChange}
                        style={{ width: '100px' }}
                      >
                        <option value="Years">Years</option>
                        <option value="Months">Months</option>
                      </Form.Select>
                    </Col>
                  </Row>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><FaWeight className="me-2" />Weight (Optional)</Form.Label>
                  <Row>
                    <Col>
                      <Form.Control
                        type="number"
                        step="0.1"
                        name="weight"
                        value={formData.weight}
                        onChange={handleChange}
                        placeholder="Weight"
                      />
                    </Col>
                    <Col xs="auto">
                      <Form.Select
                        name="weightUnit"
                        value={formData.weightUnit}
                        onChange={handleChange}
                        style={{ width: '100px' }}
                      >
                        <option value="Kgs">Kgs</option>
                        <option value="Grams">Grams</option>
                      </Form.Select>
                    </Col>
                  </Row>
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><FaHashtag className="me-2" />Quantity</Form.Label>
                      <Form.Control
                        type="number"
                        name="quantity"
                        value={formData.quantity}
                        onChange={handleChange}
                        placeholder="Enter quantity"
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Gender</Form.Label>
                      <Form.Select name="gender" value={formData.gender} onChange={handleChange}>
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="pair">Pair</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </div>

          {/* Location */}
          <div className="form-section">
            <h5 className="section-title">
              <FaMapMarkerAlt className="me-2" />
              Location
            </h5>

            <Form.Group className="mb-3">
              <Form.Label><FaMapMarkerAlt className="me-2" />State</Form.Label>
              <Form.Select name="state" value={formData.state} onChange={handleChange}>
                <option value="">Select State</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><FaCity className="me-2" />City</Form.Label>
              <Form.Select
                name="city"
                value={formData.city}
                onChange={handleChange}
                disabled={!formData.state}
              >
                <option value="">Select City</option>
                {cities.map(city => (
                  <option key={city.name} value={city.name}>{city.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label><FaMapPin className="me-2" />Neighbourhood (Optional)</Form.Label>
              <Form.Select
                name="neighbourhood"
                value={formData.neighbourhood}
                onChange={handleChange}
                disabled={!formData.city || neighborhoods.length === 0}
              >
                <option value="">Select Neighbourhood</option>
                {neighborhoods.map(neighbourhood => (
                  <option key={neighbourhood} value={neighbourhood}>{neighbourhood}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Button
              variant="outline-primary"
              className="mb-3"
              onClick={handleUseCurrentLocation}
              type="button"
            >
              <FaLocationArrow className="me-2" />
              Use Current Location
            </Button>
          </div>

          {/* Images */}
          <div className="form-section">
            <h5 className="section-title">
              <FaImages className="me-2" />
              Images
            </h5>

            {formData.images && formData.images.length > 0 && (
              <div className="image-preview-grid mb-3">
                {formData.images.map((imageName, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={getImageUrl(imageName)} alt={`Ad ${index + 1}`} />
                    <Button
                      variant="danger"
                      size="sm"
                      className="remove-image-btn"
                      onClick={() => handleRemoveExistingImage(imageName)}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {newImages.length > 0 && (
              <div className="image-preview-grid mb-3">
                {newImages.map((file, index) => (
                  <div key={index} className="image-preview-item">
                    <img src={URL.createObjectURL(file)} alt={`New ${index + 1}`} />
                    <Button
                      variant="danger"
                      size="sm"
                      className="remove-image-btn"
                      onClick={() => handleRemoveNewImage(index)}
                    >
                      ×
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Form.Group className="mb-3">
              <Form.Label><FaUpload className="me-2" />Add New Images (Optional)</Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageChange}
              />
              <Form.Text className="text-muted">
                You can add up to 5 images. Select multiple images at once.
              </Form.Text>
            </Form.Group>
          </div>

          {/* Action Buttons */}
          <div className="form-actions">
            <Button
              variant="primary"
              type="submit"
              disabled={saving}
              className="me-2"
            >
              {saving ? 'Updating...' : 'Update Ad'}
            </Button>
            <Button
              variant="secondary"
              onClick={() => navigate('/my-ads')}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </Form>
      </div>
    </Container>
  );
};

export default EditAd;
